/**
 * Partners Logos Mobile Carousel
 * Responsive logo display with mobile sliding animation
 */
class PartnersLogosCarousel {
	static CONFIG = {
		MOBILE_BREAKPOINT: 782,
		LOGOS_PER_ROW: 4,
		ANIMATION_INTERVAL: 2000,
		SELECTORS: {
			SECTION: '.partners-logos',
			CONTAINER: '.partners-logos__logos-container',
			LOGO: '.partners-logos__logo'
		}
	};

	constructor() {
		this.instances = new Map();
		this.resizeTimeout = null;
		this.init();
	}

	init() {
		this.bindEvents();
		this.handleViewportChange();
	}

	bindEvents() {
		if (document.readyState !== 'loading') {
			this.handleViewportChange();
		} else {
			document.addEventListener('DOMContentLoaded', () => this.handleViewportChange());
		}

		window.addEventListener('resize', () => {
			clearTimeout(this.resizeTimeout);
			this.resizeTimeout = setTimeout(() => this.handleViewportChange(), 250);
		});

		document.addEventListener('visibilitychange', () => {
			if (document.hidden) this.pauseAnimations();
			else this.resumeAnimations();
		});
	}

	handleViewportChange() {
		if (window.innerWidth <= PartnersLogosCarousel.CONFIG.MOBILE_BREAKPOINT) {
			this.initMobileCarousels();
		} else {
			this.teardownCarousels();
		}
	}

	initMobileCarousels() {
		document.querySelectorAll(PartnersLogosCarousel.CONFIG.SELECTORS.SECTION).forEach(section => {
			const container = section.querySelector(PartnersLogosCarousel.CONFIG.SELECTORS.CONTAINER);
			if (!container || container.dataset.initialized) return;

			const logos = Array.from(container.querySelectorAll(PartnersLogosCarousel.CONFIG.SELECTORS.LOGO));
			if (logos.length !== 12) return;

			// Store original indices
			logos.forEach((logo, i) => logo.dataset.originalIndex = i);

			// Create 3 rows of 4 logos each
			container.innerHTML = '';
			const rows = [];
			for (let i = 0; i < 3; i++) {
				const rowLogos = logos.slice(i * 4, (i + 1) * 4);
				const rowEl = document.createElement('div');
				rowEl.className = `partners-logos__row row-${i + 1}`;
				const trackEl = document.createElement('div');
				trackEl.className = 'partners-logos__track';
				rowLogos.forEach(logo => trackEl.appendChild(logo));
				rowEl.appendChild(trackEl);
				container.appendChild(rowEl);
				rows.push(trackEl);
			}

			// Animation states: row positions and directions
			const states = [
				{ index: 0, direction: 1 },
				{ index: 2, direction: -1 },
				{ index: 0, direction: 1 }
			];

			const applyTransforms = () => {
				rows.forEach((track, i) => {
					track.style.transform = `translateX(calc(-1 * ${states[i].index} * (100% + 16px) / 2))`;
				});
			};

			const step = () => {
				states.forEach(state => {
					if (state.direction > 0 && state.index >= 2) state.direction = -1;
					else if (state.direction < 0 && state.index <= 0) state.direction = 1;
					state.index += state.direction;
				});
				applyTransforms();
			};

			applyTransforms();

			// Start animation if user doesn't prefer reduced motion
			let intervalId = null;
			if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
				intervalId = setInterval(step, PartnersLogosCarousel.CONFIG.ANIMATION_INTERVAL);
			}

			const instance = { container, intervalId, states, rows, applyTransforms };
			this.instances.set(container, instance);
			container.dataset.initialized = 'true';
		});
	}

	teardownCarousels() {
		this.instances.forEach((instance, container) => {
			if (instance.intervalId) clearInterval(instance.intervalId);

			// Restore original structure
			const logos = Array.from(container.querySelectorAll('.partners-logos__track .partners-logos__logo'));
			container.innerHTML = '';
			logos
				.sort((a, b) => parseInt(a.dataset.originalIndex) - parseInt(b.dataset.originalIndex))
				.forEach(logo => {
					delete logo.dataset.originalIndex;
					container.appendChild(logo);
				});

			delete container.dataset.initialized;
		});
		this.instances.clear();
	}

	pauseAnimations() {
		this.instances.forEach(instance => {
			if (instance.intervalId) {
				clearInterval(instance.intervalId);
				instance.intervalId = null;
			}
		});
	}

	resumeAnimations() {
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
		
		this.instances.forEach(instance => {
			if (!instance.intervalId) {
				const step = () => {
					instance.states.forEach(state => {
						if (state.direction > 0 && state.index >= 2) state.direction = -1;
						else if (state.direction < 0 && state.index <= 0) state.direction = 1;
						state.index += state.direction;
					});
					instance.applyTransforms();
				};
				instance.intervalId = setInterval(step, PartnersLogosCarousel.CONFIG.ANIMATION_INTERVAL);
			}
		});
	}

	destroy() {
		this.teardownCarousels();
		window.removeEventListener('resize', this.handleViewportChange);
		document.removeEventListener('visibilitychange', this.pauseAnimations);
		clearTimeout(this.resizeTimeout);
	}
}

// Initialize
(() => {
	let carousel = null;
	const init = () => carousel = carousel || new PartnersLogosCarousel();
	
	if (document.readyState !== 'loading') init();
	else document.addEventListener('DOMContentLoaded', init);
	
	window.addEventListener('beforeunload', () => carousel?.destroy());
})();


